import React, { ReactNode } from 'react'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import MenuItem from 'components_new/atoms/Menu/MenuItem'
import TextField from 'components_new/atoms/TextField'

/**
 * What an operand field is never squeezed below, so a deeply nested formula
 * scrolls sideways instead of collapsing its fields.
 */
export const FORMULA_OPERAND_MIN_WIDTH = 200

export interface FormulaBlockOption {
  id: string
  title: string
}

export interface FormulaBlockProps {
  left: ReactNode
  onChangeOperation: (operation: string) => void
  /** Given for a nested block, which is the only one that can be removed. */
  onRemove?: () => void
  operation: string
  operations: FormulaBlockOption[]
  right: ReactNode
}

/**
 * Two operands with an operation between them, nested to whatever depth the
 * caller renders into it.
 *
 * The operands are the caller's — the economy KPI:s and the derived metrics in
 * the studio store their formulas differently, and only the shape they are read
 * in is shared.
 */
const FormulaBlock = (props: FormulaBlockProps) => {
  const { left, onChangeOperation, onRemove, operation, operations, right } =
    props

  return (
    <Box
      sx={{
        alignItems: 'center',
        bgcolor: 'action.hover',
        borderRadius: 1,
        display: 'flex',
        flex: 1,
        gap: 1,
        justifyContent: 'center',
        p: 1,
        position: 'relative'
      }}
    >
      <Box sx={{ flex: '1 0 0' }}>{left}</Box>
      <Box sx={{ flexShrink: 0, width: 38 }}>
        <TextField
          onChange={(event) => onChangeOperation(event.target.value)}
          select={true}
          SelectProps={{ hideIcon: true }}
          size="small"
          sx={{ my: 0 }}
          value={operation}
        >
          {operations.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.title}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box sx={{ flex: '1 0 0' }}>{right}</Box>
      {onRemove ? (
        <Box sx={{ position: 'absolute', right: -12, top: -12 }}>
          <IconButton onClick={onRemove} size="small">
            <Icon color="error" fontSize="small" name="RemoveCircleOutlined" />
          </IconButton>
        </Box>
      ) : null}
    </Box>
  )
}

export default FormulaBlock
